"use client";

import { useState, type FC, type Ref } from "react";
import {
  Button,
  Flex,
  ListCard,
  type ListCardProps,
  ListRow,
  Text,
  ValueParser,
  type ValueType,
} from "@huub-web/ui";

type PaginatedListCardTemplatePropsType = Pick<ListCardProps, "title"> & {
  paginated?: boolean;
  limit?: number;
  items: {
    title: string;
    subtitle?: string;
    value?: ValueType;
    image?: string;
    /**
     * @deprecated Use `image` instead.
     */
    imageSrc?: string;
    summary?: { label: string; value: string }[];
  }[];
};

export interface PaginatedListCardTemplateProps
  extends PaginatedListCardTemplatePropsType {}

/**
 * A template for a paginated list card.
 */
export const PaginatedListCardTemplate: FC<
  PaginatedListCardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({ title, paginated = false, limit, items, ref }) => {
  const [page, setPage] = useState(1);

  const handleLoadMore = () => {
    setPage(page + 1);
  };

  const paginatedItems = items.slice(0, page * (limit ?? 10));

  return (
    <ListCard
      ref={ref}
      variant="secondary"
      title={title}
      trailing={
        paginated &&
        paginatedItems.length < items.length && (
          <Button variant="tertiary" onClick={handleLoadMore}>
            Laad meer
          </Button>
        )
      }
    >
      {paginatedItems?.map((item, i) => (
        <ListRow
          key={i}
          variant="secondary"
          size="l"
          leading={
            (item.image ?? item.imageSrc) && (
              <img
                src={item.image ?? item.imageSrc}
                alt={`${item.title} logo`}
                width={70}
              />
            )
          }
          trailing={
            item.value && (
              <Text variant="bodyL">
                <ValueParser value={item.value} />
              </Text>
            )
          }
        >
          <Flex gap="xxs" align="center">
            <Flex direction="column" gap="xxs2" basis="300px">
              <Text variant="bodyM" ellipsis bold>
                {item.title}
              </Text>
              {item.subtitle && (
                <Text variant="bodyS" color="currentColor50" ellipsis>
                  {item.subtitle}
                </Text>
              )}
            </Flex>

            {item.summary && (
              <Flex gap="xxs2" width="stretch">
                {item.summary.map((summary, i) => (
                  <Flex
                    key={i}
                    direction="column"
                    gap="xxs"
                    basis={`calc(100% / ${item.summary?.length}`}
                  >
                    <Text variant="bodyXS" color="currentColor50" ellipsis>
                      {summary.label}
                    </Text>
                    <Text variant="bodyS" ellipsis>
                      {summary.value}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </ListRow>
      ))}
    </ListCard>
  );
};
